'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { type CampaignCallInfo } from '@callixbrasil/client-sdk';
import { useCallOperatorCurrentCall, useCallOperatorCurrentCallInfo } from '../lib/callixSdk';
import {
  FiAlertTriangle,
  FiBriefcase,
  FiHash,
  FiPhoneCall,
  FiSmartphone,
  FiUser,
} from 'react-icons/fi';
import { campaignFormCacheKey, campaignFormRequestFromCallInfo, fetchCampaignForm } from '../lib/campaignFormClient';
import { findFormValue, type CampaignFormResponse } from '../lib/campaignFormTypes';

/**
 * Painel da chamada de campanha no discador flutuante: destaca decisor, empresa,
 * dívida e telefone do formulário do contato.
 *
 * Os dados vêm de UMA requisição ao backend (/api/campaign-form/{contactId}),
 * com cache por contato e retry - ver src/lib/campaignFormClient.ts.
 */

interface CampaignContactData {
  form: CampaignFormResponse | null;
  isLoading: boolean;
  error: string | null;
}

function formatPhoneValue(raw: string | undefined): string {
  if (!raw) return '-';

  const digits = raw.replace(/\D/g, '');

  if (digits.length >= 10) {
    const ddd = digits.slice(0, 2);
    const core = digits.slice(2, digits.length - 4);
    const tail = digits.slice(-4);
    const formattedCore = core.length > 5 ? `${core.slice(0, core.length - 4)} ${core.slice(core.length - 4)}` : core;

    return `(${ddd}) ${formattedCore}-${tail}`.replace(/\s+/g, ' ').trim();
  }

  return raw.trim();
}

export function CallInfoDisplay() {
  const callInfo = useCallOperatorCurrentCallInfo();
  const currentCall = useCallOperatorCurrentCall();
  const campaignContactPhoneId = currentCall?.data?.campaignContactPhoneId ?? null;

  if (callInfo?.type === 'campaign') {
    return <CampaignCallInfoDisplay campaignCallInfo={callInfo.info} campaignContactPhoneId={campaignContactPhoneId} />;
  }

  if (callInfo) {
    return (
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
        <p className="text-xs font-semibold text-amber-700">Chamada manual (sem informações de campanha)</p>
      </div>
    );
  }

  return null;
}

export function CampaignCallInfoDisplay(props: { campaignCallInfo: CampaignCallInfo; campaignContactPhoneId?: number | null }) {
  const { campaign, campaignContact } = props.campaignCallInfo;

  const [contactData, setContactData] = useState<CampaignContactData>({
    form: null,
    isLoading: false,
    error: null,
  });
  const [retry, setRetry] = useState<{ key: string | null; n: number }>({ key: null, n: 0 });

  const request = useMemo(
    () => campaignFormRequestFromCallInfo(props.campaignCallInfo, props.campaignContactPhoneId),
    [props.campaignCallInfo, props.campaignContactPhoneId]
  );
  const requestKey = request ? campaignFormCacheKey(request) : null;
  const requestRef = useRef(request);
  requestRef.current = request;
  const partialRefreshKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const current = requestRef.current;
    if (!current || !requestKey) {
      setContactData({ form: null, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    setContactData({ form: null, isLoading: true, error: null });

    fetchCampaignForm(current, { force: retry.key === requestKey && retry.n > 0 })
      .then((form) => {
        if (!cancelled) {
          setContactData({ form, isLoading: false, error: null });
          // Rótulos técnicos (valores por código, sem layout): refaz uma vez em seguida
          if (form.partial && partialRefreshKeyRef.current !== requestKey) {
            partialRefreshKeyRef.current = requestKey;
            refreshTimer = setTimeout(() => setRetry((prev) => ({ key: requestKey, n: prev.n + 1 })), 3000);
          }
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.error('[CampaignCallInfoDisplay] Erro ao buscar dados:', error);
          setContactData({
            form: null,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro ao buscar dados do contato',
          });
        }
      });

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [requestKey, retry]);

  const prioritizedFields = useMemo(
    () => [
      { label: 'Decisor', keys: ['DECISOR', 'CONTATO', 'NOME'], icon: <FiUser className="text-sm" /> },
      { label: 'Empresa', keys: ['EMPRESA', 'RAZAO SOCIAL', 'RAZÃO SOCIAL', 'CLIENTE'], icon: <FiBriefcase className="text-sm" /> },
      { label: 'Dívida ativa', keys: ['DIVIDA ATIVA', 'DÍVIDA ATIVA', 'VALOR TOTAL DEVIDO'], icon: <FiHash className="text-sm" /> },
      { label: 'Telefone', keys: ['TELEFONE', 'CELULAR', 'WHATSAPP'], icon: <FiSmartphone className="text-sm" /> },
    ],
    []
  );

  const highlightData = useMemo(() => {
    return prioritizedFields.map((field) => {
      const rawValue =
        (field.label === 'Telefone'
          ? findFormValue(contactData.form, field.keys) ?? campaignContact?.phoneNumber
          : findFormValue(contactData.form, field.keys)) || '';

      const display =
        field.label === 'Telefone' ? formatPhoneValue(rawValue) : (rawValue || '-').toString().trim() || '-';

      return {
        ...field,
        value: display,
        isEmpty: !rawValue,
      };
    });
  }, [campaignContact?.phoneNumber, contactData.form, prioritizedFields]);

  const hasData = highlightData.some((item) => !item.isEmpty);

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-inner">
            <FiPhoneCall className="text-base" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Campanha</p>
            <p className="truncate text-sm font-bold text-slate-900">
              {campaign?.name || 'Chamada de campanha'}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Em chamada
        </span>
      </div>

      {contactData.isLoading && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center" role="status">
          <div className="mx-auto mb-2 inline-block h-7 w-7 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
          <p className="text-xs font-medium text-slate-600">Carregando dados do contato...</p>
        </div>
      )}

      {contactData.error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
          <div className="mb-1 flex items-center gap-2">
            <FiAlertTriangle className="text-sm text-rose-600" />
            <p className="text-xs font-semibold text-rose-700">Erro ao carregar dados</p>
          </div>
          <p className="ml-6 text-[11px] text-rose-600">{contactData.error}</p>
          <button
            type="button"
            onClick={() => setRetry((prev) => ({ key: requestKey, n: prev.n + 1 }))}
            className="mt-2 ml-6 text-[11px] font-semibold text-rose-700 underline hover:text-rose-800"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {!contactData.isLoading && !contactData.error && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {highlightData.map((item) => (
            <div
              key={item.label}
              className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-slate-50 p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
            >
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {item.icon}
                <span className="truncate">{item.label}</span>
              </div>
              <p className="break-words text-sm font-bold leading-tight text-slate-900">
                {item.value || '-'}
              </p>
            </div>
          ))}
        </div>
      )}

      {!contactData.isLoading && !contactData.error && !hasData && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-medium text-slate-600">
          Nenhuma informação de campanha disponível para este contato.
        </div>
      )}
    </div>
  );
}
